// Car CRUD interface. Mirrors api.js — reads cars/manifest.json in
// demo mode; future Supabase swap lands here without touching pages.
//
// Public surface:
//   listCars()   → Promise<CarSummary[]>
//   getCar(id)   → Promise<Car>      (full schema-shaped object)
//
// CarSummary = { id, name, manufacturer, description, primary, accent }

import { isConfigured } from './config.js';
import { loadCar } from '../../shared/car-schema.js';

const CARS_DIR = globalThis.__RM_CARS_BASE__ ?? '../cars/';
const MANIFEST_PATH = `${CARS_DIR}manifest.json`;
let manifestCache = null;

export async function listCars() {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const summaries = await Promise.all(
    manifest.cars.map(async filename => {
      try {
        const id = idFromFilename(filename);
        const data = await fetchCarFile(filename);
        return summarize(id, data);
      } catch (err) {
        console.error(`Failed to load car "${filename}":`, err);
        return null;
      }
    }),
  );
  return summaries.filter(Boolean);
}

export async function getCar(id) {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const filename = manifest.cars.find(f => idFromFilename(f) === id);
  if (!filename) throw new Error(`Unknown car: ${id}`);
  const data = await fetchCarFile(filename);
  const { car } = loadCar(data);
  return car;
}

async function loadManifest() {
  if (manifestCache) return manifestCache;
  // Cache-bust + no-store: without this, the browser's HTTP cache can
  // serve a stale manifest after files were renamed on disk, listing
  // filenames that 404 silently and leaving the list empty.
  const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Failed to load cars manifest: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data?.cars)) throw new Error('Cars manifest is malformed: missing "cars" array');
  manifestCache = data;
  return data;
}

async function fetchCarFile(filename) {
  const url = `${CARS_DIR}${filename}?t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function idFromFilename(filename) {
  return filename.replace(/\.car\.json$/, '').replace(/\.json$/, '');
}

function summarize(id, data) {
  return {
    id,
    name: data.name || id,
    manufacturer: data.manufacturer || '',
    description: data.description || '',
    primary: data.livery?.primary || '#cc0000',
    accent: data.livery?.accent || '#ffffff',
  };
}
